import {
  ErrorHandler,
  inject,
  NgModule,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { Router, RouterModule, Routes } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ToastrModule } from 'ngx-toastr';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { isSentryEnabled } from './is-sentry-enabled';

// Material Modules
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MatDialogModule,
  MAT_DIALOG_SCROLL_STRATEGY,
  MAT_DIALOG_DEFAULT_OPTIONS,
  MatDialogConfig,
} from '@angular/material/dialog';
import { DIALOG_VIEWPORT_MAX_WIDTH } from '@/app/_constant/dialog-width.constants';
import { MatDividerModule } from '@angular/material/divider';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatNativeDateModule, MatOptionModule } from '@angular/material/core';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCardModule } from '@angular/material/card';
import { MatListModule } from '@angular/material/list';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {
  MAT_TOOLTIP_DEFAULT_OPTIONS,
  MatTooltipDefaultOptions,
  MatTooltipModule,
} from '@angular/material/tooltip';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { OVERLAY_DEFAULT_CONFIG, Overlay, ScrollStrategy } from '@angular/cdk/overlay';
import { SharedModule } from './Shared/shared.module';

// Syncfusion/ej2
import { RichTextEditorAllModule } from '@syncfusion/ej2-angular-richtexteditor';
import { DropDownTreeModule } from '@syncfusion/ej2-angular-dropdowns';

import { AppComponent } from './app.component';
import { ServerErrorComponent } from './main/errors/server-error/server-error.component';
import { EndUserLicenceAgreementComponent } from './main/end-user-licence-agreement/end-user-licence-agreement.component';
import { PrivacyPolicyComponent } from './main/privacy-policy/privacy-policy.component';
import { NotFoundComponent } from './main/errors/not-found/not-found.component';
import { ConfirmPopupComponent } from './main/confirm-popup/confirm-popup.component';
import { LogoutErrorComponent } from './main/logout-error/logout-error.component';
import {
  FilterByNamePipe,
  SortingDialogComponent,
} from './main/sorting-dialog/sorting-dialog.component';
import { InternalErrorComponent } from './main/errors/internal-error/internal-error.component';
import { ReportsComponent } from './main/reports/reports.component';
import { LicenceAgreementComponent } from './main/licence-agreement/licence-agreement.component';
import { ChangePasswordComponent } from './main/change-password/change-password.component';
import { MatterFolderDailogComponent } from './layout/components/toolbar/matter-folder-dailog/matter-folder-dailog.component';
import { TryFreeSetUpWizardComponent } from './main/try-free-set-up-wizard/try-free-set-up-wizard.component';
import { MatterDialogComponent } from '@pages/time-entries/matter-dialog/matter-dialog.component';
import { RenameEmailComponent } from '@pages/inbox/rename-email/rename-email.component';
import { BindDropdownTreeComponent } from '@pages/inbox/rename-email/bind-dropdown-tree/bind-dropdown-tree.component';
import { DialogBoxComponent } from './help-desk/dialog-box/dialog-box.component';
import { EndOfMonthReportsComponent } from './main/end-of-month-reports/end-of-month-reports.component';
import { SyncToXeroPopupComponent } from './main/sync-to-xero-popup/sync-to-xero-popup.component';
import { UploadTemplatesPopupComponent } from './main/upload-templates-popup/upload-templates-popup.component';
import { HelpDeskComponent } from './help-desk/help-desk.component';
import { BulkInvoicingWorkitemComponent } from '@pages/invoice/bulk-invoicing-workitem/bulk-invoicing-workitem.component';
import { UserFirmDirectoryContactDetailComponent } from './user-firm-directory-contact-detail/user-firm-directory-contact-detail.component';
import { CSVPopupComponent } from './main/csv-popup/csv-popup.component';
import { BulkProgressComponent } from '@pages/template/bulk-progress/bulk-progress.component';
import { MaterialModule } from './Shared/material.module';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { PagesModule } from '@pages/pages.module';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import { JwtInterceptor, ErrorInterceptor } from './_helpers';
import { DatePipe } from '@angular/common';
import { inputDateFormat } from './date.adapter';
import { ToFixedAmountPipe } from './to-fixed-amount.pipe';
import { Convet24To12Pipe } from './convet24-to12.pipe';
import { FixedAmountPipe } from './fixed-amount.pipe';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { LayoutModule } from './layout/layout.module';
import { provideIcons } from './core/icons/provider';
import { provideTheming } from './core/theming';
import { ApplicationPipesModule } from '@pages/application-pipes.module';
import { TrustChartAccount } from '@pages/trust-chart-of-account/trust-chart-of-account.module';
import { MyProfileModule } from '@pages/my-profile/my-profile.module';
import { NumberSeparatorModule } from '@directives/numberseparator/number-separator.module';
import { NumbersModule } from '@directives/allownumber/numbers.module';
import { CurrencyFormatModule } from '@directives/currencyformat/currency-format.module';
import { DetailsModule } from '@pages/invoice/invoice-add-dailog/details/details.module';
import { NumberMaskModule } from '@directives/number-mask/number-mask.module';

/**
 * This is using for set the route JSON
 **/
const appRoutes: Routes = [
  {
    path: '',
    loadChildren: () =>
      import('./main/authentication/authentication.module').then((m) => m.AuthenticationModule),
  },
  { path: 'server-error', component: ServerErrorComponent },
  {
    path: 'end-user-licence-agreement',
    component: EndUserLicenceAgreementComponent,
  },
  { path: 'privacy-policy', component: PrivacyPolicyComponent },
  { path: '**', component: NotFoundComponent },
];

export function noopScrollStrategyFactory(overlay: Overlay): () => ScrollStrategy {
  return () => overlay.scrollStrategies.noop();
}

@NgModule({
  declarations: [
    AppComponent,
    SortingDialogComponent,
    FilterByNamePipe,
    InternalErrorComponent,
    ReportsComponent,
    LicenceAgreementComponent,
    ChangePasswordComponent,
    MatterFolderDailogComponent,
    TryFreeSetUpWizardComponent,
    MatterDialogComponent,
    ConfirmPopupComponent,
    EndOfMonthReportsComponent,
    SyncToXeroPopupComponent,
    UploadTemplatesPopupComponent,
    RenameEmailComponent,
    LogoutErrorComponent,
    BindDropdownTreeComponent,
    HelpDeskComponent,
    BulkInvoicingWorkitemComponent,
    DialogBoxComponent,
    UserFirmDirectoryContactDetailComponent,
    CSVPopupComponent,
    BulkProgressComponent,
  ],
  exports: [
    SortingDialogComponent,
    ChangePasswordComponent,
    DropDownTreeModule,
    RenameEmailComponent,
    LogoutErrorComponent,
    BindDropdownTreeComponent,
    DialogBoxComponent,
    MaterialModule,
    FlexLayoutModule,
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    DetailsModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled:
        environment.serviceWorkerEnabled &&
        typeof window !== 'undefined' &&
        window.location &&
        window.location.hostname &&
        !window.location.hostname.includes('localhost') &&
        window.location.hostname !== '127.0.0.1',
    }),
    RouterModule.forRoot(appRoutes, { useHash: true }),
    DragDropModule,

    LayoutModule,
    PagesModule,
    ToastrModule.forRoot(),
    DropDownTreeModule,
    ApplicationPipesModule,
    TrustChartAccount,
    NumberMaskModule,
    SharedModule,
    MyProfileModule,
    NumberSeparatorModule,
    NumbersModule,
    RichTextEditorAllModule,
    CurrencyFormatModule,
    MaterialModule,

    // Material UI Modules
    MatChipsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatMomentDateModule,
    MatButtonModule,
    MatIconModule,
    MatPaginatorModule,
    MatDividerModule,
    MatDialogModule,
    MatCheckboxModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatMenuModule,
    MatStepperModule,
    MatAutocompleteModule,
    MatTableModule,
    MatSortModule,
    MatToolbarModule,
    MatCardModule,
    MatListModule,
    MatSnackBarModule,
    MatTabsModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatOptionModule,
    MatSlideToggleModule,
    MatTooltipModule,
    FlexLayoutModule,
    PagesModule,
    NgxMaskDirective,
    NgxMaskPipe,

    PrivacyPolicyComponent,
    EndUserLicenceAgreementComponent,
    ServerErrorComponent,
    NotFoundComponent,
  ],
  providers: [
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: { usePopover: false },
    },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        showDelay: 0,
        hideDelay: 100,
        touchendHideDelay: 1500,
        disableTooltipInteractivity: true,
      } satisfies MatTooltipDefaultOptions,
    },
    {
      provide: MAT_DIALOG_SCROLL_STRATEGY,
      useFactory: noopScrollStrategyFactory,
      deps: [Overlay],
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { maxWidth: DIALOG_VIEWPORT_MAX_WIDTH } as MatDialogConfig,
    },
    provideZoneChangeDetection({
      eventCoalescing: true,
    }),
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    DatePipe,
    ...inputDateFormat,
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'outline',
        floatLabel: 'auto',
        subscriptSizing: 'dynamic',
      },
    },
    ToFixedAmountPipe,
    Convet24To12Pipe,
    FixedAmountPipe,
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideNgxMask(),
    provideAnimations(),

    provideIcons(),
    provideTheming({
      scheme: 'light',
      primary: '#1565C0',
      error: '#dc2626',
    }),
    ...(isSentryEnabled()
      ? [
          {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler(),
          },
          {
            provide: Sentry.TraceService,
            deps: [Router],
          },
          provideAppInitializer(() => {
            inject(Sentry.TraceService);
          }),
        ]
      : []),
  ],
})
export class AppModule {}
